import { BaseLateFee, Credit } from '@aiwyn/api';
import { PaymentsState } from '@aiwyn/common/payments';
import { ConfigService } from '@aiwyn/config';
import { Client, getLoadedResults } from '@aiwyn/shared';
import { Component } from '@angular/core';
import { Store } from '@ngxs/store';
import { combineLatest, map, shareReplay } from 'rxjs';

interface InvoiceTableClientBreakdown {
  total: number;
  count: number;
  clientBreakdown: {
      total: number;
      count: number;
      clientName: string;
  }[];
}

interface InvoicesTableSummaryViewModel {
  totalOutstanding: number;
  unpaidInvoices: number;
  credits: InvoiceTableClientBreakdown;
  lateFees: InvoiceTableClientBreakdown;
  pastDue: InvoiceTableClientBreakdown;
}

@Component({
  selector: 'app-invoices-table-summary',
  templateUrl: './invoices-table-summary.component.html',
  styleUrl: './invoices-table-summary.component.scss',
  standalone: false,
})
export class InvoicesTableSummaryComponent {
  activeCurrency = this.store.selectSignal(PaymentsState.getActiveCurrency);

  // Data sources
  payables$ = getLoadedResults(this.store.select(PaymentsState.getPayablesLoadable)).pipe(
    shareReplay(1),
  );

  lateFees$ = getLoadedResults(this.store.select(PaymentsState.getLateFeesLoadable)).pipe(
    shareReplay(1),
  );

  credits$ = getLoadedResults(this.store.select(PaymentsState.getCreditsLoadable)).pipe(
    shareReplay(1),
  );

  // View Model Source Stream
  viewModel$ = combineLatest([
    this.payables$,
    this.lateFees$,
    this.credits$,
    getLoadedResults(this.store.select(PaymentsState.getClientsLoadable)),
  ]).pipe(
    map(([payables, _lateFees, _credits, clients]) => { 
      let totalOutstanding = 0;
      let unpaidInvoices = 0;
      let totalPastDue = 0;
      let pastDueInvoices = 0;
      const pastDueInvoicesByClient = {};

      const lateFees = this.mapLateFees(_lateFees, clients);
      const credits = this.mapCredits(_credits, clients);

      // Calculate total outstanding
      for (const payable of payables) {
        unpaidInvoices++;
        totalOutstanding += (payable.invoice.arTran.amount - payable.invoice.arTran.allocated);

        if (payable.overdue) {
          pastDueInvoices++;
          totalPastDue += (payable.invoice.arTran.amount - payable.invoice.arTran.allocated);
          if (!pastDueInvoicesByClient[payable.invoice.arTran.clientId]) {
            pastDueInvoicesByClient[payable.invoice.arTran.clientId] = {
              total: (payable.invoice.arTran.amount - payable.invoice.arTran.allocated),
              count: 1,
              clientName: payable.clientName,
            };
          } else {
            pastDueInvoicesByClient[payable.invoice.arTran.clientId].total += (payable.invoice.arTran.amount - payable.invoice.arTran.allocated);
            pastDueInvoicesByClient[payable.invoice.arTran.clientId].count++;
          }
        }
      }
      return {
        totalOutstanding, 
        unpaidInvoices, 
        credits: { total: credits.totalCreditValue, count: credits.numberOfCredits, clientBreakdown: this.byClientToArray(credits.byClient) }, 
        lateFees: { total: lateFees.totalLateFees, count: lateFees.numberOfLateFees, clientBreakdown: this.byClientToArray(lateFees.byClient) }, 
        pastDue: { total: totalPastDue, count: pastDueInvoices, clientBreakdown: this.byClientToArray(pastDueInvoicesByClient) }
      } as InvoicesTableSummaryViewModel;
    }),
  );

  constructor(
    private store: Store,
    readonly configService: ConfigService,
  ) {
  }

  private byClientToArray(obj: any) {
    return Object.keys(obj).map(key => obj[key]).sort((a, b) => b.count - a.count);
  }

  private mapLateFees(lateFees: BaseLateFee[], clients: Client[]) {
    return lateFees.reduce((acc, lateFee) => {
      // if no client exists, create a new client
      if (!acc.byClient[lateFee.clientId]) {
        acc.byClient[lateFee.clientId] = {
          total: 0,
          count: 0,
          clientName: clients.find(client => client.id === lateFee.clientId)?.name || '',
        };
      }
      acc.byClient[lateFee.clientId].total += lateFee.amount;
      acc.byClient[lateFee.clientId].count++;

      acc.totalLateFees += lateFee.amount;
      acc.numberOfLateFees++;
      return acc;
    }, { totalLateFees: 0, numberOfLateFees: 0, byClient: {} });
  }

  private mapCredits(credits: Credit[], clients: Client[]) {
    return credits.reduce((acc, credit) => {
      // if no client exists, create a new client
      if (!acc.byClient[credit.clientId]) {
        acc.byClient[credit.clientId] = {
          total: 0,
          count: 0,
          clientName: clients.find(client => client.id === credit.clientId)?.name || '',
        };
      }

      acc.byClient[credit.clientId].total += -credit.amountRemaining; // Credits are negative values.
      acc.byClient[credit.clientId].count++;

      acc.totalCreditValue += -credit.amountRemaining; // Credits are negative values.
      acc.numberOfCredits++;
      return acc;
    }, { numberOfCredits: 0, totalCreditValue: 0, byClient: {} });
  }
}
